// PRC-043 (bocetos G8 y G9): tarjetas de planes, selector mensual / anual y
// «Comparar planes», con el contenido de pricingPlans.ts. Las usan la página de
// planes, «Prueba terminada» (D1) y la portada, para que digan lo mismo.
import React, { useState } from 'react';
import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckRounded';
import { JourneyButton, JourneyChip, JourneyText } from './JourneyBasics';
import { J } from '../journeyTokens';
import { startCheckout } from '../journeyBilling';
import { AUTONOMY_CONTACT_HREF, formatEuros, PLAN_COMPARISON, PLANS, type PlanDefinition } from '../pricingPlans';

export type BillingCycle = 'monthly' | 'yearly';

export const BillingToggle: React.FC<{ value: BillingCycle; onChange: (v: BillingCycle) => void }> = ({ value, onChange }) => {
  const item = (v: BillingCycle, label: string) => (
    <Box component="button" type="button" onClick={() => onChange(v)} aria-pressed={value === v} sx={{
      border: 0, cursor: 'pointer', px: 2, py: 1, borderRadius: 999, fontSize: 13, fontWeight: 700, fontFamily: J.font,
      bgcolor: value === v ? J.border : 'transparent', color: value === v ? J.text : J.muted,
    }}>{label}</Box>
  );
  return (
    <Box role="group" aria-label="Periodo de facturación" sx={{ display: 'inline-flex', p: '4px', borderRadius: 999, border: `1px solid ${J.borderStrong}`, bgcolor: J.panel, gap: '4px' }}>
      {item('monthly', 'Mensual')}
      {item('yearly', 'Anual · 2 meses gratis')}
    </Box>
  );
};

// Errores al contratar con nombre propio (PRC-043 · punto 5).
export const checkoutErrorMessage = (err: any): { message: string; exists: boolean } => {
  const status = err?.response?.status;
  if (status === 409) return { message: 'Ya tienes una suscripción: gestiónala desde el portal de facturación.', exists: true };
  if (status === 403) return { message: 'Solo el administrador de la cuenta puede contratar.', exists: false };
  if (status === 400 && typeof err?.response?.data?.detail === 'string') return { message: err.response.data.detail, exists: false };
  return { message: 'El servicio de pagos no responde ahora mismo. No se ha cobrado nada: inténtalo de nuevo en unos minutos.', exists: false };
};

export const useGrowthCheckout = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; exists: boolean } | null>(null);
  const buy = async (cycle: BillingCycle) => {
    setBusy(true);
    setError(null);
    try {
      await startCheckout(cycle === 'yearly' ? 'growth_yearly' : 'growth_monthly');
    } catch (err) {
      setBusy(false);
      setError(checkoutErrorMessage(err));
    }
  };
  return { buy, busy, error };
};

const PlanCard: React.FC<{ plan: PlanDefinition; cycle: BillingCycle; featured?: boolean; action: React.ReactNode }> = ({ plan, cycle, featured, action }) => {
  const price = plan.comingSoon
    ? { main: 'A medida', sub: '', note: '' }
    : plan.id === 'trial'
      ? { main: formatEuros(0), sub: 'sin tarjeta', note: '' }
      : cycle === 'yearly'
        ? { main: formatEuros(plan.yearly ?? 0), sub: '+ IVA / año', note: `o ${formatEuros(plan.monthly ?? 0)} + IVA al mes` }
        : { main: formatEuros(plan.monthly ?? 0), sub: '+ IVA / mes', note: `o ${formatEuros(plan.yearly ?? 0)} + IVA al año: dos meses gratis` };
  return (
    <Box sx={{
      borderRadius: J.radiusCard, border: `1px solid ${featured ? J.sky : J.borderStrong}`, bgcolor: 'rgba(30,41,59,.4)', p: '28px',
      display: 'flex', flexDirection: 'column', gap: '18px', fontFamily: J.font, minWidth: 0,
      ...(featured ? { boxShadow: '0 0 0 1px rgba(56,189,248,.35),0 24px 60px rgba(2,6,23,.6)' } : {}),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <JourneyText variant="h2" component="h3">{plan.name}</JourneyText>
        {plan.comingSoon && <JourneyChip tone="amber">Próximamente</JourneyChip>}
        {plan.id === 'trial' && <JourneyChip tone="neutral">{plan.tagline}</JourneyChip>}
      </Box>
      {plan.id !== 'trial' && <JourneyText variant="small" sx={{ mt: '-10px' }}>{plan.tagline}</JourneyText>}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontSize: 40, fontWeight: 800, letterSpacing: '-.03em', color: J.text }}>{price.main}</Box>
          {price.sub && <Box component="span" sx={{ fontSize: 14, color: J.muted }}>{price.sub}</Box>}
        </Box>
        {price.note && <Box sx={{ fontSize: 13, color: J.muted, mt: '4px' }}>{price.note}</Box>}
      </Box>
      {action}
      {plan.footnote && <JourneyText variant="small" sx={{ mt: '-6px' }}>{plan.footnote}</JourneyText>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {plan.groups.map((g) => (
          <Box key={g.group} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: J.muted }}>{g.group}</Box>
            {g.items.map((f) => (
              <Box key={f} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: 14, color: J.body, lineHeight: 1.5 }}>
                <CheckIcon sx={{ flex: '0 0 18px', fontSize: 18, mt: '2px', color: J.skyText }} />
                <span>{f}</span>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export interface PlanCardsProps {
  cycle: BillingCycle;
  // Botón de Growth: contratar (app) o empezar la prueba (portada).
  growthAction: React.ReactNode;
  // Tarjeta de prueba (portada), con su botón.
  trialAction?: React.ReactNode;
}

export const PlanCards: React.FC<PlanCardsProps> = ({ cycle, growthAction, trialAction }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${trialAction ? 3 : 2}, minmax(0,1fr))` }, gap: '20px', alignItems: 'start' }}>
    {trialAction && <PlanCard plan={PLANS.trial} cycle={cycle} action={trialAction} />}
    <PlanCard plan={PLANS.growth} cycle={cycle} featured action={growthAction} />
    <PlanCard
      plan={PLANS.autonomy}
      cycle={cycle}
      action={<JourneyButton kind="ghost" block href={AUTONOMY_CONTACT_HREF}>Hablar con nosotros</JourneyButton>}
    />
  </Box>
);

export const PlanComparison: React.FC = () => (
  <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: J.font }}>
    <JourneyText variant="h3" component="h2">Comparar planes</JourneyText>
    <Box sx={{ overflowX: 'auto', border: `1px solid ${J.border}`, borderRadius: '12px' }}>
      <Box component="table" sx={{
        width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 14, color: J.body,
        '& th, & td': { textAlign: 'left', p: '10px 16px', borderBottom: `1px solid ${J.border}` },
        '& td:not(:first-of-type), & th:not(:first-of-type)': { width: '26%' },
      }}>
        <thead>
          <tr>
            <Box component="th" scope="col" aria-label="Función" sx={{ color: J.muted, fontWeight: 600 }} />
            <Box component="th" scope="col" sx={{ color: J.text, fontWeight: 700 }}>Growth</Box>
            <Box component="th" scope="col" sx={{ color: J.text, fontWeight: 700 }}>Autonomy · próximamente</Box>
          </tr>
        </thead>
        <tbody>
          {PLAN_COMPARISON.map((section) => (
            <React.Fragment key={section.group}>
              <tr>
                <Box component="th" scope="colgroup" colSpan={3} sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: J.muted, bgcolor: 'rgba(148,163,184,.05)' }}>{section.group}</Box>
              </tr>
              {section.rows.map((r) => (
                <tr key={r.label}>
                  <Box component="th" scope="row" sx={{ fontWeight: 500, color: J.body }}>{r.label}</Box>
                  <td>{r.growth}</td>
                  <td>{r.autonomy}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </Box>
    </Box>
  </Box>
);
